/* eslint-disable prefer-template */
/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable no-unused-vars */
import React, { useState, useMemo, useEffect } from "react";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  LinearScale,
  CategoryScale,
  BarElement,
  BarController,
} from "chart.js";
import { DownloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { Container, BodyContainer } from "../../styles/ContainerStyles";
import HeaderComponent from "../../components/Header/Header";
import {
  useBillingOverviewCards,
  useBillingCompaniesList,
  useBillingChart,
  useBillingCompanyInvoices,
} from "../../hooks/useBilling";
import BillingService from "../../services/Billing";
import SummaryCards from "./SummaryCards";
import CompaniesPanel from "./CompaniesPanel";
import ChartPanel from "./ChartPanel";
import CompanyInvoicesModal from "./CompanyInvoicesModal";
import InvoiceDetailModal from "./InvoiceDetailModal";
import BillingDataExportModal from "./BillingDataExportModal";
import { InformationContainer } from "./styles";
import * as S from "./styles";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  LinearScale,
  CategoryScale,
  BarElement,
  BarController
);

const Billing = () => {
  // Get current month name
  const getCurrentMonth = (): string => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return months[new Date().getMonth()] || "January";
  };

  const [searchValue, setSearchValue] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [monthFilter, setMonthFilter] = useState<string>(getCurrentMonth());
  const [companyFilter, setCompanyFilter] = useState<number | "all">("all");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const pageSize = 10;

  // Modal states
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [isInvoicesModalOpen, setIsInvoicesModalOpen] =
    useState<boolean>(false);
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [isInvoiceDetailModalOpen, setIsInvoiceDetailModalOpen] =
    useState<boolean>(false);
  const [invoiceSearchValue, setInvoiceSearchValue] = useState<string>("");
  const [invoiceMonthFilter, setInvoiceMonthFilter] = useState<string>("All");
  const [invoiceYearFilter, setInvoiceYearFilter] = useState<string>(
    new Date().getFullYear().toString()
  );
  const [invoicePage, setInvoicePage] = useState<number>(1);
  const [isExportModalOpen, setIsExportModalOpen] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // API hooks
  const {
    data: overviewData,
    isLoading: isLoadingOverview,
    error: overviewError,
  } = useBillingOverviewCards();

  const {
    data: companiesDataResponse,
    isLoading: isLoadingCompanies,
    error: companiesError,
  } = useBillingCompaniesList(
    currentPage,
    pageSize,
    searchValue || undefined,
    statusFilter !== "all" ? statusFilter : undefined
  );

  // Fetch all companies for the dropdown (using large page size)
  const { data: allCompaniesResponse } = useBillingCompaniesList(1, 1000);

  const {
    data: chartDataResponse,
    isLoading: isLoadingChart,
    error: chartError,
  } = useBillingChart(
    monthFilter !== "All" ? monthFilter : undefined,
    companyFilter !== "all" ? companyFilter : undefined
  );

  // Company invoices hook (only fetch when modal is open)
  const {
    data: invoicesDataResponse,
    isLoading: isLoadingInvoices,
    error: invoicesError,
  } = useBillingCompanyInvoices(
    selectedCompany?.id,
    invoiceMonthFilter !== "All" ? invoiceMonthFilter : undefined,
    invoiceYearFilter,
    10,
    invoicePage
  );

  const totalAmountMade = overviewData?.data?.totalAmountMade || 0;
  const totalCompanies = overviewData?.data?.totalCompanies || 0;
  const companiesData = companiesDataResponse?.data?.data || [];
  const totalPages = companiesDataResponse?.data?.totalPages || 1;
  const totalCount = companiesDataResponse?.data?.totalCount || 0;
  const allCompanies = allCompaniesResponse?.data?.data || [];

  // Process chart data from API for bar chart
  const chartDataFromAPI = chartDataResponse?.data || [];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleViewInvoices = (company: any) => {
    setSelectedCompany(company);
    setIsInvoicesModalOpen(true);
    setInvoicePage(1);
    setInvoiceSearchValue("");
    setInvoiceMonthFilter("All");
    setInvoiceYearFilter(new Date().getFullYear().toString());
  };

  const handleCloseInvoicesModal = () => {
    setIsInvoicesModalOpen(false);
    setSelectedCompany(null);
  };

  const handleViewInvoice = (invoice: any) => {
    setSelectedInvoice(invoice);
    setIsInvoiceDetailModalOpen(true);
  };

  const handleCloseInvoiceDetailModal = () => {
    setIsInvoiceDetailModalOpen(false);
    setSelectedInvoice(null);
  };

  // Reset to page 1 when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchValue, statusFilter]);

  const handleExportClick = () => {
    setIsExportModalOpen(true);
  };

  const handleCloseExportModal = () => {
    setIsExportModalOpen(false);
    setExportError(null);
  };

  const handleExport = async (filters: any) => {
    setIsExporting(true);
    setExportError(null);
    try {
      // Convert dates from DD/MM/YYYY to ISO format
      let startDateISO: string | undefined;
      let endDateISO: string | undefined;

      if (filters.startDate) {
        // Parse DD/MM/YYYY and convert to ISO
        const parsedStartDate = dayjs(filters.startDate, "DD/MM/YYYY");
        if (parsedStartDate.isValid()) {
          startDateISO = parsedStartDate.toISOString();
        }
      }

      if (filters.endDate) {
        // Parse DD/MM/YYYY and convert to ISO
        const parsedEndDate = dayjs(filters.endDate, "DD/MM/YYYY");
        if (parsedEndDate.isValid()) {
          endDateISO = parsedEndDate.toISOString();
        }
      }

      // Map subscription type IDs to their labels (API expects label strings)
      const subscriptionTypeMap: Record<number, string> = {
        2: "Single User",
        5: "Single User (Premium)",
        8: "Family Of 2",
        9: "Family Of 2 (Premium)",
        3: "Family of 4",
        6: "Family of 4 (Premium)",
        4: "Family of 6",
        7: "Family of 6 (Premium)",
      };

      // All possible subscription type IDs
      const allSubscriptionTypeIds = [2, 5, 8, 9, 3, 6, 4, 7];

      // Check if all subscription types are selected
      const allSelected =
        filters.subscriptionTypes &&
        filters.subscriptionTypes.length > 0 &&
        allSubscriptionTypeIds.every((id) =>
          filters.subscriptionTypes.includes(id)
        );

      // Convert subscription type IDs to their label strings
      // API expects: subscriptionTypes: ["Single User"]
      // If all are selected, omit the field to get all results
      const subscriptionTypesStrings: string[] | undefined =
        !allSelected &&
        filters.subscriptionTypes &&
        filters.subscriptionTypes.length > 0
          ? filters.subscriptionTypes
              .map((st: number) => subscriptionTypeMap[st])
              .filter((label: string | undefined) => label !== undefined)
          : undefined;

      // Prepare request parameters
      const requestParams: {
        startDate?: string;
        endDate?: string;
        companyId?: number[];
        subscriptionTypes?: string[];
      } = {};

      if (startDateISO) {
        requestParams.startDate = startDateISO;
      }
      if (endDateISO) {
        requestParams.endDate = endDateISO;
      }
      if (filters.companyIds && filters.companyIds.length > 0) {
        requestParams.companyId = filters.companyIds;
      }
      if (subscriptionTypesStrings && subscriptionTypesStrings.length > 0) {
        requestParams.subscriptionTypes = subscriptionTypesStrings;
      }

      // Debug logging
      // eslint-disable-next-line no-console
      console.log("Export request params:", requestParams);

      // Call the bulk export API
      const response = await BillingService.bulkExport(requestParams);

      // Get the blob from the response
      const blob = await response.blob();

      // Extract filename from Content-Disposition header if available
      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = "InvoiceExport.zip"; // Default filename
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
        );
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, "");
        }
      }

      // Create a download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setIsExportModalOpen(false);
      setExportError(null);
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error("Export failed:", error);
      // Extract error message from response if available
      let errorMessage = "Unknown error";
      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (typeof error === "string") {
        errorMessage = error;
      }
      setExportError(errorMessage);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div>
      <Container className="w-full h-full">
        <BodyContainer className="w-full h-full">
          <HeaderComponent title="Billing" />
          <div style={{ width: "100%", height: "100%", flex: 1 }}>
            {/* Summary Cards with Export Button */}
            <S.SummaryCardsWithExportContainer>
              <SummaryCards
                totalAmountMade={totalAmountMade}
                totalCompanies={totalCompanies}
                isLoadingOverview={isLoadingOverview}
              />
              <S.ExportButton onClick={handleExportClick}>
                <DownloadOutlined />
                Export
              </S.ExportButton>
            </S.SummaryCardsWithExportContainer>

            {/* Main Content Area */}
            <InformationContainer>
              <S.MainContentArea>
                {/* Companies Panel */}
                <CompaniesPanel
                  companiesData={companiesData}
                  isLoadingCompanies={isLoadingCompanies}
                  searchValue={searchValue}
                  setSearchValue={setSearchValue}
                  statusFilter={statusFilter}
                  setStatusFilter={setStatusFilter}
                  totalCount={totalCount}
                  filteredCount={totalCount}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  onViewInvoices={handleViewInvoices}
                />

                {/* Subscription Revenue Type Panel */}
                <ChartPanel
                  monthFilter={monthFilter}
                  setMonthFilter={setMonthFilter}
                  companyFilter={companyFilter}
                  setCompanyFilter={setCompanyFilter}
                  companies={allCompanies}
                  chartDataFromAPI={chartDataFromAPI}
                  isLoadingChart={isLoadingChart}
                  chartError={chartError}
                />
              </S.MainContentArea>
            </InformationContainer>
          </div>
        </BodyContainer>
      </Container>

      {/* Company Invoices Modal */}
      <CompanyInvoicesModal
        isOpen={isInvoicesModalOpen}
        onClose={handleCloseInvoicesModal}
        selectedCompany={selectedCompany}
        invoicesDataResponse={invoicesDataResponse}
        isLoadingInvoices={isLoadingInvoices}
        invoiceSearchValue={invoiceSearchValue}
        setInvoiceSearchValue={setInvoiceSearchValue}
        invoiceMonthFilter={invoiceMonthFilter}
        setInvoiceMonthFilter={setInvoiceMonthFilter}
        invoiceYearFilter={invoiceYearFilter}
        setInvoiceYearFilter={setInvoiceYearFilter}
        onViewInvoice={handleViewInvoice}
      />

      {/* Invoice Detail Modal */}
      <InvoiceDetailModal
        isOpen={isInvoiceDetailModalOpen}
        onClose={handleCloseInvoiceDetailModal}
        selectedInvoice={selectedInvoice}
        selectedCompany={selectedCompany}
      />

      {/* Billing Data Export Modal */}
      <BillingDataExportModal
        isOpen={isExportModalOpen}
        onClose={handleCloseExportModal}
        companies={allCompanies}
        onExport={handleExport}
        isLoading={isExporting}
        error={exportError}
      />
    </div>
  );
};

export default Billing;
